import { useFieldWiring } from "./Field";
import { SurfaceProvider } from "./Surface";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";

/** Placeholder color shared by every text control. */
export const PLACEHOLDER = "placeholder:text-card-text/30";

/**
 * The field surface (border, radius, focus, size) shared by TextField, TextArea, Select and the
 * react-hook-form adapters in `@/components/forms`. Focus is a border change, never a ring.
 */
export const inputSurfaceVariants = cva(
  ["w-full rounded-control border bg-card text-card-text", "outline-none transition-colors duration-fast ease-standard disabled:opacity-70"],
  {
    variants: {
      invalid: {
        true: "border-primary-red focus-within:border-primary-red",
        false: "border-border focus-within:border-primary",
      },
      inputSize: {
        sm: "px-3 py-2 text-sm",
        md: "px-4 py-3 text-base",
        lg: "px-4 py-3 text-lg",
      },
    },
    defaultVariants: { invalid: false, inputSize: "md" },
  },
);

type SurfaceProps = VariantProps<typeof inputSurfaceVariants>;

export type TextFieldProps = SurfaceProps &
  Omit<ComponentPropsWithoutRef<"input">, "size" | "prefix"> & {
    /** Node inside the field, before the text (a search glyph). */
    leading?: ReactNode;
    /** Node inside the field, after the text (a clear button). */
    trailing?: ReactNode;
    /** Text adornment before the value (`R$`); dims while the placeholder shows. */
    prefix?: ReactNode;
    /** Text adornment after the value (a unit). */
    suffix?: ReactNode;
    /** Applied to the field surface, not to the inner `<input>`. */
    className?: string;
    /** Applied to the inner `<input>`, which the surface `className` never reaches. */
    inputClassName?: string;
  };

const ADORNMENT = "shrink-0 text-card-text group-has-[:placeholder-shown]/field:text-card-text/30";

/**
 * Text input on the shared surface. With adornments the border moves to a wrapper so they sit in the
 * same focus ring (an `lg` adorned input keeps the wrapper's 18px; smaller sizes stay at the 16px iOS floor).
 * Inside a `Field` it picks up id, `aria-describedby` and invalid state.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { invalid, inputSize, leading, trailing, prefix, suffix, className, inputClassName, ...rest },
  ref,
) {
  const { invalid: resolvedInvalid, ...wiring } = useFieldWiring({
    id: rest.id,
    "aria-describedby": rest["aria-describedby"],
    invalid,
    required: rest.required,
  });
  const surface = cn(inputSurfaceVariants({ invalid: resolvedInvalid, inputSize }), className);
  const adorned = leading || trailing || prefix || suffix;

  if (!adorned) {
    return <input {...rest} {...wiring} ref={ref} className={cn(surface, PLACEHOLDER, inputClassName)} />;
  }

  return (
    <div className={cn(surface, "group/field flex items-center gap-2")}>
      <SurfaceProvider surface="card">
        {leading}
        {prefix ? (
          <span aria-hidden className={ADORNMENT}>
            {prefix}
          </span>
        ) : null}
        <input
          {...rest}
          {...wiring}
          ref={ref}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-inherit outline-none",
            inputSize === "lg" && "[font-size:inherit]",
            PLACEHOLDER,
            inputClassName,
          )}
        />
        {suffix ? (
          <span aria-hidden className={ADORNMENT}>
            {suffix}
          </span>
        ) : null}
        {trailing}
      </SurfaceProvider>
    </div>
  );
});

export type TextAreaProps = SurfaceProps & ComponentPropsWithoutRef<"textarea">;

/** Multi-line field on the same surface; consumes the enclosing `Field` like TextField. */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid, inputSize, className, ...rest },
  ref,
) {
  const { invalid: resolvedInvalid, ...wiring } = useFieldWiring({
    id: rest.id,
    "aria-describedby": rest["aria-describedby"],
    invalid,
    required: rest.required,
  });
  return (
    <textarea
      {...rest}
      {...wiring}
      ref={ref}
      className={cn(inputSurfaceVariants({ invalid: resolvedInvalid, inputSize }), "resize-none", PLACEHOLDER, className)}
    />
  );
});
